import { createHash } from "node:crypto";
import { mkdirSync } from "node:fs";
import { dirname } from "node:path";
import Database from "better-sqlite3";
import { SCHEMA } from "./schema.js";
import type { ObjectType, OntObject, OntRelation, RelationType, WalkStep } from "../types.js";
import type { ChunkState, ConversationEvidence, ObjectProposal, RelationProposal } from "../enrich/types.js";
import { domainRangeViolated } from "../enrich/domain-range.js";
import {
  applyDecay,
  clampToParent,
  deriveConversationUse,
  deriveGuardEligible,
  evidenceKindFromVerifiedBy,
  initialScore,
  sameUtcDay,
  SCORE,
  type AcquiescenceStrength,
  type ConversationUse,
  type EvidenceKind,
  type FactType,
} from "../enrich/trust-policy.js";

/** The persisted enrichment-proposal shape (0.2.0). Same fields as RelationProposal. */
export type ProposalRow = RelationProposal;

interface ProposalDbRow {
  rel_id: string;
  rel: string;
  source_id: string;
  target_id: string;
  source_resolved: number;
  target_resolved: number;
  trust: string;
  trust_rank: number;
  verified_by: string;
  confidence: number;
  evidence_path: string | null;
  evidence_span: string | null;
  source_record_hash: string | null;
  proposed_at: string | null;
  confirmed: string | null;
  rejected_at: string | null;
  asked_at: string | null;
  source_kind: string | null;
  source_family: string | null;
  extraction_run_id: string | null;
  evidence_kind: string | null;
  conversation_use: string | null;
  guard_eligible: number | null;
  conversation_touched: number | null;
  contested: number | null;
  score: number | null;
  acquiescence_count: number | null;
  last_acquiesced_at: string | null;
  fact_type: string | null;
  derived_from: string | null;
}

function dbToProposal(r: ProposalDbRow): ProposalRow {
  return {
    relId: r.rel_id,
    rel: r.rel as RelationType,
    sourceId: r.source_id,
    targetId: r.target_id,
    sourceResolved: r.source_resolved === 1,
    targetResolved: r.target_resolved === 1,
    trust: r.trust as RelationProposal["trust"],
    trustRank: r.trust_rank,
    verifiedBy: r.verified_by as RelationProposal["verifiedBy"],
    confidence: r.confidence,
    evidencePath: r.evidence_path ?? "",
    evidenceSpan: r.evidence_span,
    sourceRecordHash: r.source_record_hash,
    proposedAt: r.proposed_at,
    confirmed: r.confirmed,
    rejectedAt: r.rejected_at,
    askedAt: r.asked_at,
    sourceKind: (r.source_kind as ProposalRow["sourceKind"]) ?? "record",
    sourceFamily: r.source_family,
    extractionRunId: r.extraction_run_id,
    evidenceKind: (r.evidence_kind as EvidenceKind | null) ?? undefined,
    conversationUse: (r.conversation_use as ConversationUse | null) ?? undefined,
    guardEligible: r.guard_eligible == null ? undefined : r.guard_eligible === 1,
    conversationTouched: r.conversation_touched == null ? undefined : r.conversation_touched === 1,
    contested: r.contested == null ? undefined : r.contested === 1,
    score: r.score ?? undefined,
    acquiescenceCount: r.acquiescence_count ?? undefined,
    lastAcquiescedAt: r.last_acquiesced_at,
    factType: (r.fact_type as FactType | null) ?? null,
    derivedFrom: r.derived_from,
  };
}

function dbToObjectProposal(r: Record<string, unknown>): ObjectProposal {
  return {
    objId: r.obj_id as string,
    type: r.type as ObjectType,
    label: r.label as string,
    trust: r.trust as ObjectProposal["trust"],
    trustRank: r.trust_rank as number,
    verifiedBy: r.verified_by as ObjectProposal["verifiedBy"],
    confidence: (r.confidence as number) ?? 0,
    sourceKind: (r.source_kind as ObjectProposal["sourceKind"]) ?? "conversation",
    sourceFamily: (r.source_family as string | null) ?? null,
    evidencePath: (r.evidence_path as string | null) ?? null,
    evidenceSpan: (r.evidence_span as string | null) ?? null,
    extractionRunId: (r.extraction_run_id as string | null) ?? null,
    proposedAt: (r.proposed_at as string | null) ?? null,
    confirmed: (r.confirmed as string | null) ?? null,
    rejectedAt: (r.rejected_at as string | null) ?? null,
    askedAt: (r.asked_at as string | null) ?? null,
    evidenceKind: (r.evidence_kind as EvidenceKind | null) ?? undefined,
    conversationUse: (r.conversation_use as ConversationUse | null) ?? undefined,
    guardEligible: r.guard_eligible == null ? undefined : r.guard_eligible === 1,
    contested: r.contested == null ? undefined : r.contested === 1,
    score: (r.score as number | null) ?? undefined,
    acquiescenceCount: (r.acquiescence_count as number | null) ?? undefined,
    lastAcquiescedAt: (r.last_acquiesced_at as string | null) ?? null,
    factType: (r.fact_type as FactType | null) ?? null,
    derivedFrom: (r.derived_from as string | null) ?? null,
  };
}

/**
 * §5-bis relation-id recipe. Stable over the (source, relation, target) triple,
 * so re-accepting the same relation is idempotent (never duplicates).
 */
export function makeRelId(sourceId: string, rel: RelationType, targetId: string): string {
  // Newline separates the triple in the preimage: ids derive from filenames /
  // frontmatter keys and never contain a newline, so distinct triples cannot
  // collide on the preimage (a "|"-separated preimage could, since ids may hold "|").
  return createHash("sha256").update(`${sourceId}\n${rel}\n${targetId}`).digest("hex").slice(0, 16);
}

export interface TraverseOptions {
  /** Hop limit, capped at 3 (§5B). */
  readonly maxHops?: number;
  /** Restrict to these edge types (empty = all). */
  readonly rels?: readonly RelationType[];
}

interface RelationRow {
  rel_id: string;
  rel: string;
  source_id: string;
  target_id: string;
  provenance: string;
  confidence: number;
  confirmed: string | null;
  evidence: string | null;
}

function rowToRelation(r: RelationRow): OntRelation {
  return {
    relId: r.rel_id,
    rel: r.rel as RelationType,
    sourceId: r.source_id,
    targetId: r.target_id,
    provenance: r.provenance as OntRelation["provenance"],
    confidence: r.confidence,
    confirmed: r.confirmed,
    evidence: r.evidence,
  };
}

/**
 * SQLite-backed ontos index. Markdown `ontos:` frontmatter is the source of
 * truth; this is a regenerable derived index. Sync API (better-sqlite3); call
 * `close()` to release the file lock (matters on Windows).
 */
export class OntosStore {
  readonly dbPath: string;
  private db: Database.Database;

  constructor(dbPath: string, opts: { readonly?: boolean } = {}) {
    this.dbPath = dbPath;
    if (opts.readonly) {
      // Strictly read-only open of an EXISTING index: no directory/db creation,
      // no -wal/-shm sidecar creation, no schema exec, no migration, and no
      // busy-wait on a writer's lock (`timeout: 0` throws immediately). A query
      // against an older index lacking a column throws too — hook-latency
      // callers catch all of it and fail open.
      this.db = new Database(dbPath, { readonly: true, fileMustExist: true, timeout: 0 });
      return;
    }
    mkdirSync(dirname(dbPath), { recursive: true });
    this.db = new Database(dbPath);
    this.db.pragma("journal_mode = WAL");
    this.db.pragma("foreign_keys = ON");
    this.db.exec(SCHEMA);
    this.migrate();
  }

  /**
   * Open an EXISTING index strictly read-only — for readers at hook latency
   * (e.g. the risky-action guard) that must never create files or block.
   */
  static openReadonly(dbPath: string): OntosStore {
    return new OntosStore(dbPath, { readonly: true });
  }

  /**
   * Additive column migration: `CREATE TABLE IF NOT EXISTS` does NOT add new
   * columns to a table that already exists from an earlier schema, so an index
   * built by a prior 0.2.0-dev build would lack newer columns and crash on query.
   * Add any missing column here (the index is regenerable, so additive-only is
   * enough — no data backfill). Idempotent.
   */
  private migrate(): void {
    const cols = (table: string): Set<string> =>
      new Set(
        (this.db.prepare(`PRAGMA table_info(${table})`).all() as { name: string }[]).map((r) => r.name),
      );
    const ensure = (table: string, column: string, decl: string): void => {
      if (!cols(table).has(column)) {
        this.db.exec(`ALTER TABLE ${table} ADD COLUMN ${column} ${decl}`);
      }
    };
    ensure("relation_proposals", "rejected_at", "TEXT");
    ensure("relation_proposals", "asked_at", "TEXT");
    // Chunk-params signature on the extraction watermark (2026-07-13), additive.
    ensure("conversation_extraction_state", "chunk_sig", "TEXT");
    // Date anchor on conversation evidence (2026-07-04), additive.
    ensure("proposal_evidence", "message_at", "TEXT");
    // Phase-2 (conversation extraction) provenance, additive.
    ensure("relation_proposals", "source_kind", "TEXT NOT NULL DEFAULT 'record'");
    ensure("relation_proposals", "source_family", "TEXT");
    ensure("relation_proposals", "extraction_run_id", "TEXT");
    // Trust/usage policy (2026-06-20 pivot), additive on both proposal tables.
    for (const table of ["relation_proposals", "object_proposals"] as const) {
      ensure(table, "evidence_kind", "TEXT");
      ensure(table, "conversation_use", "TEXT");
      ensure(table, "guard_eligible", "INTEGER NOT NULL DEFAULT 0");
      ensure(table, "conversation_touched", "INTEGER NOT NULL DEFAULT 0");
      ensure(table, "contested", "INTEGER NOT NULL DEFAULT 0");
      ensure(table, "score", "REAL NOT NULL DEFAULT 0");
      ensure(table, "acquiescence_count", "INTEGER NOT NULL DEFAULT 0");
      ensure(table, "last_acquiesced_at", "TEXT");
      ensure(table, "fact_type", "TEXT");
      ensure(table, "derived_from", "TEXT");
    }
    // Backfill the guard taint for pre-existing rows that already carry conversation
    // evidence: the column defaults to 0, so without this a row with prior
    // proposal_evidence could later recompute as guard-eligible (Codex round-2).
    if (cols("relation_proposals").has("conversation_touched")) {
      // Taint AND zero any stale guard_eligible in one pass: a conversation-touched row
      // must never be guard-eligible, so a pre-existing row that was 1 is cleared here
      // (else the read path would see a stale 1) — Codex round-3.
      this.db.exec(
        `UPDATE relation_proposals SET conversation_touched = 1, guard_eligible = 0
           WHERE conversation_touched = 0
             AND rel_id IN (SELECT DISTINCT rel_id FROM proposal_evidence)`,
      );
    }
  }

  close(): void {
    this.db.close();
  }

  /** Run a function inside one transaction (bulk upserts during a rebuild). */
  transaction(fn: () => void): void {
    this.db.transaction(fn)();
  }

  /**
   * Empty all tables so a walk-and-upsert becomes a TRUE rebuild: stale objects
   * and relations from deleted/renamed records, or from a changed recurrence_key
   * / removed rule, do not linger (which would skew recurrenceCount). Call before
   * re-populating from a full record walk.
   */
  clear(): void {
    this.db.exec("DELETE FROM relations; DELETE FROM aliases; DELETE FROM objects;");
  }

  upsertObject(obj: OntObject): void {
    this.db
      .prepare(
        `INSERT INTO objects (id, type, subtype, label, source)
         VALUES (@id, @type, @subtype, @label, @source)
         ON CONFLICT(id) DO UPDATE SET
           type = excluded.type, subtype = excluded.subtype,
           label = excluded.label, source = excluded.source`,
      )
      .run({
        id: obj.id,
        type: obj.type,
        subtype: obj.subtype ?? null,
        label: obj.label,
        source: obj.source ?? null,
      });
  }

  /**
   * Idempotent on relId (§5-bis): re-accepting the same relation updates, never
   * duplicates. SAFETY GUARD (0.2.0): an UNCONFIRMED `llm` relation must never
   * enter the trusted graph — LLM output lives in `relation_proposals` until a
   * human confirms it (a confirmed llm relation, with a `confirmed` date, is the
   * promotion path and is allowed). This makes "proposals never influence
   * why()/guard" structural, not caller-dependent (Codex review 2026-06-18).
   */
  upsertRelation(rel: OntRelation): void {
    if (rel.provenance === "llm" && !rel.confirmed) {
      throw new Error(
        `refusing to write an unconfirmed llm relation into the trusted graph (${rel.relId}); use upsertProposal — confirm to promote`,
      );
    }
    this.db
      .prepare(
        `INSERT INTO relations (rel_id, rel, source_id, target_id, provenance, confidence, confirmed, evidence)
         VALUES (@relId, @rel, @sourceId, @targetId, @provenance, @confidence, @confirmed, @evidence)
         ON CONFLICT(rel_id) DO UPDATE SET
           provenance = excluded.provenance, confidence = excluded.confidence,
           confirmed = excluded.confirmed, evidence = excluded.evidence`,
      )
      .run({
        relId: rel.relId,
        rel: rel.rel,
        sourceId: rel.sourceId,
        targetId: rel.targetId,
        provenance: rel.provenance,
        confidence: rel.confidence,
        confirmed: rel.confirmed ?? null,
        evidence: rel.evidence ?? null,
      });
  }

  addAlias(alias: string, canonicalId: string): void {
    this.db
      .prepare(
        `INSERT INTO aliases (alias, canonical_id) VALUES (?, ?)
         ON CONFLICT(alias) DO UPDATE SET canonical_id = excluded.canonical_id`,
      )
      .run(alias, canonicalId);
  }

  /** All alias rows (for the local candidate resolver's slug snapshot). */
  allAliases(): { alias: string; canonicalId: string }[] {
    return (
      this.db.prepare(`SELECT alias, canonical_id FROM aliases ORDER BY alias`).all() as {
        alias: string;
        canonical_id: string;
      }[]
    ).map((r) => ({ alias: r.alias, canonicalId: r.canonical_id }));
  }

  /** §3 identity: resolve a name to a canonical object id (a real object id wins, then an alias). */
  resolveAlias(name: string): string | null {
    const direct = this.db.prepare(`SELECT id FROM objects WHERE id = ?`).get(name) as
      | { id: string }
      | undefined;
    if (direct) return direct.id;
    const row = this.db.prepare(`SELECT canonical_id FROM aliases WHERE alias = ?`).get(name) as
      | { canonical_id: string }
      | undefined;
    return row ? row.canonical_id : null;
  }

  getObject(id: string): OntObject | null {
    const row = this.db
      .prepare(`SELECT id, type, subtype, label, source FROM objects WHERE id = ?`)
      .get(id) as
      | { id: string; type: string; subtype: string | null; label: string; source: string | null }
      | undefined;
    if (!row) return null;
    return {
      id: row.id,
      type: row.type as OntObject["type"],
      subtype: (row.subtype as OntObject["subtype"]) ?? null,
      label: row.label,
      source: row.source,
    };
  }

  /** Index health snapshot (for `vortex ontos check` / the session-start ambient line). */
  counts(): { objects: number; relations: number; proposals: number } {
    const n = (sql: string): number => (this.db.prepare(sql).get() as { n: number }).n;
    return {
      objects: n("SELECT COUNT(*) n FROM objects"),
      relations: n("SELECT COUNT(*) n FROM relations"),
      // Pending candidates awaiting human confirm — BOTH relation and object proposals.
      // Object proposals are a review surface too (listObjectProposals), so counting only
      // relations under-reports an object-only pending state as a misleading "0".
      proposals:
        n("SELECT COUNT(*) n FROM relation_proposals WHERE confirmed IS NULL AND rejected_at IS NULL") +
        n("SELECT COUNT(*) n FROM object_proposals WHERE confirmed IS NULL AND rejected_at IS NULL"),
    };
  }

  /** All canonical object ids (feeds the local candidate resolver's slug snapshot). */
  allObjectIds(): string[] {
    return (this.db.prepare(`SELECT id FROM objects ORDER BY id`).all() as { id: string }[]).map(
      (r) => r.id,
    );
  }

  /** id/type/label snapshot (feeds the local candidate resolver's slug maps). */
  allObjectsLite(): { id: string; type: string; label: string }[] {
    return this.db.prepare(`SELECT id, type, label FROM objects ORDER BY id`).all() as {
      id: string;
      type: string;
      label: string;
    }[];
  }

  /** Outgoing relations from an object, optionally filtered by relation type. */
  neighbors(id: string, rels?: readonly RelationType[]): OntRelation[] {
    const rows = (
      rels && rels.length
        ? this.db
            .prepare(
              `SELECT * FROM relations WHERE source_id = ? AND rel IN (${rels.map(() => "?").join(",")})`,
            )
            .all(id, ...rels)
        : this.db.prepare(`SELECT * FROM relations WHERE source_id = ?`).all(id)
    ) as RelationRow[];
    return rows.map(rowToRelation);
  }

  /**
   * Multi-hop walk via recursive CTE — capped at maxHops (default 3, §5B) and
   * cycle-guarded: a node never re-enters its own path. The path is delimited
   * with ">" so the membership check is token-exact (not a loose substring).
   * No graph DB is needed for this scale.
   */
  traverse(startId: string, opts: TraverseOptions = {}): WalkStep[] {
    const maxHops = Math.min(opts.maxHops ?? 3, 3);
    const rels = opts.rels ?? [];
    const relFilter = rels.length ? `AND r.rel IN (${rels.map(() => "?").join(",")})` : "";
    // Path tokens are newline-delimited: ids never contain a newline, so the
    // membership check is exact (a ">"-delimiter could false-match an id that
    // legitimately contains ">").
    const sql = `
      WITH RECURSIVE walk(node, depth, path) AS (
        SELECT ?, 0, char(10) || ? || char(10)
        UNION ALL
        SELECT r.target_id, w.depth + 1, w.path || r.target_id || char(10)
        FROM relations r JOIN walk w ON r.source_id = w.node
        WHERE w.depth < ? AND instr(w.path, char(10) || r.target_id || char(10)) = 0 ${relFilter}
      )
      SELECT node, depth, path FROM walk WHERE depth > 0 ORDER BY depth`;
    const params = [startId, startId, maxHops, ...rels];
    const rows = this.db.prepare(sql).all(...params) as { node: string; depth: number; path: string }[];
    return rows.map((r) => ({
      nodeId: r.node,
      depth: r.depth,
      path: r.path.split("\n").filter(Boolean),
    }));
  }

  /** Provenance of a relation in the TRUSTED graph, or null if absent (tier HIGH check). */
  relationProvenance(relId: string): string | null {
    const row = this.db.prepare(`SELECT provenance FROM relations WHERE rel_id = ?`).get(relId) as
      | { provenance: string }
      | undefined;
    return row ? row.provenance : null;
  }

  /**
   * Store an LLM enrichment proposal (0.2.0) — into `relation_proposals`, NEVER
   * into the trusted `relations` graph. Idempotent on relId AND monotonic: a
   * re-proposal upgrades trust (e.g. self LOW → cross-model MEDIUM) but never
   * downgrades it. Returns "inserted" | "upgraded" | "kept".
   */
  upsertProposal(p: ProposalRow): "inserted" | "upgraded" | "refreshed" | "demoted" | "kept" {
    const before = this.getProposal(p.relId);
    // Trust-policy seed: evidence_kind defaults from the legacy `verifiedBy`; the
    // score seed is the floor for that kind. On CONFLICT the score becomes
    // MAX(existing, seed): accumulated acquiescence is preserved, AND an evidence
    // upgrade (self→cross_model) raises the floor — never double-counting a re-run.
    const evidenceKind: EvidenceKind = p.evidenceKind ?? evidenceKindFromVerifiedBy(p.verifiedBy);
    this.db
      .prepare(
        `INSERT INTO relation_proposals
           (rel_id, rel, source_id, target_id, source_resolved, target_resolved,
            trust, trust_rank, verified_by, confidence, evidence_path, evidence_span,
            source_record_hash, proposed_at, confirmed, source_kind, source_family, extraction_run_id,
            evidence_kind, fact_type, derived_from, score, conversation_touched)
         VALUES
           (@relId, @rel, @sourceId, @targetId, @sourceResolved, @targetResolved,
            @trust, @trustRank, @verifiedBy, @confidence, @evidencePath, @evidenceSpan,
            @sourceRecordHash, @proposedAt, @confirmed, @sourceKind, @sourceFamily, @extractionRunId,
            @evidenceKind, @factType, @derivedFrom, @scoreSeed, @conversationTouched)
         ON CONFLICT(rel_id) DO UPDATE SET
           trust = excluded.trust, trust_rank = excluded.trust_rank,
           verified_by = excluded.verified_by, confidence = excluded.confidence,
           source_resolved = excluded.source_resolved, target_resolved = excluded.target_resolved,
           evidence_path = excluded.evidence_path, evidence_span = excluded.evidence_span,
           source_record_hash = excluded.source_record_hash, proposed_at = excluded.proposed_at,
           -- source_kind / source_family keep the FIRST writer's provenance identity
           -- (a record-sourced proposal is not flipped to conversation by a later
           -- conversation re-proposal); only the run id refreshes to the latest batch.
           extraction_run_id = excluded.extraction_run_id,
           evidence_kind = COALESCE(excluded.evidence_kind, relation_proposals.evidence_kind),
           fact_type = COALESCE(excluded.fact_type, relation_proposals.fact_type),
           derived_from = COALESCE(excluded.derived_from, relation_proposals.derived_from),
           -- score floor on upgrade; acquiescence/contested/last_acquiesced_at are
           -- earned post-insert and are deliberately NOT in this SET (preserved).
           -- F3 demotion: on a genuine same-source downgrade with NO user signal,
           -- reset to the new (lower) seed so conversation_use follows the demoted tier;
           -- otherwise keep the floor via MAX. (Caveat: score conflates seed + acquiescence,
           -- so an acquiesced row keeps the old verification seed too — acceptable for the
           -- dialogue hedge; the safety column guard_eligible is conversation-false regardless.)
           score = CASE
             WHEN excluded.trust_rank < relation_proposals.trust_rank AND relation_proposals.acquiescence_count = 0
               THEN excluded.score
             ELSE MAX(relation_proposals.score, excluded.score)
           END,
           -- sticky: once tainted (by source_kind OR pre-existing evidence, encoded in
           -- the incoming seed), the row stays tainted.
           conversation_touched = MAX(relation_proposals.conversation_touched, excluded.conversation_touched)
         -- A confirmed (promoted into the trusted graph) or rejected (human decision)
         -- proposal is INVIOLABLE: a later re-extraction must not churn its trust,
         -- evidence, or — critically — its derived guard_eligible safety column.
         WHERE relation_proposals.confirmed IS NULL AND relation_proposals.rejected_at IS NULL
           AND (
             excluded.trust_rank >= relation_proposals.trust_rank
             -- F3 demotion path: the SAME conversation source self-correcting to a LOWER
             -- verdict (cross-model→self when egress went off / it is now sensitive). Tightly
             -- scoped so it can NEVER demote (a) a different transcript's corroboration
             -- (NOT EXISTS other-transcript evidence), (b) a row since upgraded by a record/
             -- cross source (evidence_path moved off this transcript), (c) a HIGH/record row
             -- (trust_rank <= 2 + source_kind), or (d) a confirmed/rejected row (outer AND).
             OR (
               excluded.trust_rank < relation_proposals.trust_rank
               AND relation_proposals.trust_rank <= 2
               AND relation_proposals.source_kind = 'conversation' AND excluded.source_kind = 'conversation'
               AND excluded.source_family IS NOT NULL
               AND excluded.source_family = relation_proposals.source_family
               AND excluded.evidence_path = relation_proposals.evidence_path
               AND NOT EXISTS (
                 SELECT 1 FROM proposal_evidence pe
                  WHERE pe.rel_id = relation_proposals.rel_id
                    AND pe.transcript_id <> excluded.source_family
               )
             )
           )`,
      )
      .run({
        relId: p.relId,
        rel: p.rel,
        sourceId: p.sourceId,
        targetId: p.targetId,
        sourceResolved: p.sourceResolved ? 1 : 0,
        targetResolved: p.targetResolved ? 1 : 0,
        trust: p.trust,
        trustRank: p.trustRank,
        verifiedBy: p.verifiedBy,
        confidence: p.confidence,
        evidencePath: p.evidencePath ?? null,
        evidenceSpan: p.evidenceSpan ?? null,
        sourceRecordHash: p.sourceRecordHash ?? null,
        proposedAt: p.proposedAt ?? null,
        confirmed: p.confirmed ?? null,
        sourceKind: p.sourceKind ?? "record",
        sourceFamily: p.sourceFamily ?? null,
        extractionRunId: p.extractionRunId ?? null,
        evidenceKind,
        factType: p.factType ?? null,
        derivedFrom: p.derivedFrom ?? null,
        scoreSeed: p.score ?? initialScore(evidenceKind),
        // Taint from the source OR pre-existing (possibly orphan) conversation evidence,
        // so evidence attached BEFORE the proposal existed still disqualifies it (round-3).
        conversationTouched:
          (p.sourceKind ?? "record") === "conversation" || this.hasConversationEvidence(p.relId) ? 1 : 0,
      });
    // Re-derive conversation_use / guard_eligible from the FINAL merged row state
    // (post-MAX score, merged evidence_kind, preserved contested). Idempotent — a
    // "kept" (blocked) row just gets the same values rewritten. A confirmed/rejected
    // row's derived columns are FROZEN: the WHERE above blocked the merge, so skip the
    // recompute too (never touch an inviolable row).
    if (!(before && (before.confirmed || before.rejectedAt))) {
      // The taint is sticky/monotonic, NOT subject to the trust-rank gate: a conversation
      // write must disqualify the row even when the conflict SET was rank-BLOCKED (a lower-
      // rank conversation echo of a pure-record guard row). Apply it unconditionally here
      // — the INSERT seed / conflict MAX cover the rest (Codex round-4).
      if ((p.sourceKind ?? "record") === "conversation" || this.hasConversationEvidence(p.relId)) {
        this.db.prepare(`UPDATE relation_proposals SET conversation_touched = 1 WHERE rel_id = ?`).run(p.relId);
      }
      this.recomputeDerived("relation_proposals", "rel_id", p.relId);
    }
    if (!before) return "inserted";
    if (p.trustRank > before.trustRank) return "upgraded"; // higher trust replaced the old row
    if (p.trustRank === before.trustRank) return "refreshed"; // same trust: confidence/evidence updated
    // lower trust: either the F3 same-source demotion path wrote it, or the WHERE blocked it.
    return (this.getProposal(p.relId)?.trustRank ?? before.trustRank) < before.trustRank ? "demoted" : "kept";
  }

  /**
   * Re-derive the two STORED policy columns (conversation_use, guard_eligible) from a
   * proposal row's current (evidence_kind, score, contested, fact_type, source_kind).
   * Time decay and the derived≤parent clamp are NOT applied here — those are read-time
   * (see `effectiveConversationUse`), because they depend on "now" and on the parent's
   * live state. This stores the steady-state hedge; decay/clamp adjust it on read.
   */
  private recomputeDerived(
    table: "relation_proposals" | "object_proposals",
    idCol: "rel_id" | "obj_id",
    id: string,
  ): void {
    const row = this.db
      .prepare(
        `SELECT evidence_kind, score, contested, fact_type, source_kind, conversation_touched
           FROM ${table} WHERE ${idCol} = ?`,
      )
      .get(id) as
      | {
          evidence_kind: string | null;
          score: number;
          contested: number;
          fact_type: string | null;
          source_kind: string | null;
          conversation_touched: number;
        }
      | undefined;
    if (!row) return;
    // Fail CLOSED on both safety axes when a value is somehow absent: an unknown
    // evidence kind is treated as the weakest (self, never guard-eligible), and an
    // unknown source as conversation (never auto guard-eligible). The schema makes
    // both columns NOT NULL, so this is defense-in-depth, not a live path.
    const evidenceKind = (row.evidence_kind as EvidenceKind | null) ?? "self";
    const sourceKind = (row.source_kind as RelationProposal["sourceKind"]) ?? "conversation";
    const use = deriveConversationUse({
      score: row.score,
      evidenceKind,
      factType: (row.fact_type as FactType | null) ?? null,
      contested: row.contested === 1,
      sourceKind,
    });
    // A row EVER touched by a conversation-sourced write is permanently disqualified
    // from auto guard-eligibility — the sticky taint closes the provenance/evidence
    // decoupling leak (source_kind='record' kept by first-writer-wins while a later
    // conversation writer upgraded evidence_kind to a guard-eligible kind).
    const guard =
      row.conversation_touched !== 1 &&
      deriveGuardEligible({ evidenceKind, contested: row.contested === 1, sourceKind });
    this.db
      .prepare(`UPDATE ${table} SET conversation_use = ?, guard_eligible = ? WHERE ${idCol} = ?`)
      .run(use, guard ? 1 : 0, id);
  }

  // ── Trust/usage policy: acquiescence promotion + correction (2026-06-20) ──────

  /**
   * Record that the user ACQUIESCED to a relation proposal — presented saliently,
   * relevant to the next step, not objected to (`weak`), or actively built upon
   * (`strong`). Raises the score (weak +0.5 / strong +1.5) and re-derives use. A
   * second distinct-session acquiescence is what lifts a fact toward HIGH (two strong
   * ⇒ score 3 ⇒ high, fact-type permitting).
   *
   * Same-calendar-day repeats are NOT counted (anti over-count of one origin / one
   * session — Codex). A confirmed/rejected proposal is left untouched. Returns the
   * outcome, or null if the proposal does not exist.
   */
  recordAcquiescence(
    relId: string,
    strength: AcquiescenceStrength,
    atISO: string,
  ): { counted: boolean; score: number; conversationUse: ConversationUse } | null {
    return this.applyAcquiescence("relation_proposals", "rel_id", relId, strength, atISO);
  }

  /**
   * Record a user CORRECTION of a relation proposal: score −5, mark `contested`, and
   * RESET the acquiescence accumulation (count → 0, last_acquiesced_at → null). The
   * contested flag forces conversationUse to low and guard_eligible to false until the
   * fact is independently re-established — blocking entrenchment from re-acquiescence.
   * Returns true if a proposal was updated.
   */
  recordCorrection(relId: string, atISO?: string): boolean {
    return this.applyCorrection("relation_proposals", "rel_id", relId, atISO);
  }

  /** As {@link recordAcquiescence}, for an object proposal. */
  recordObjectAcquiescence(
    objId: string,
    strength: AcquiescenceStrength,
    atISO: string,
  ): { counted: boolean; score: number; conversationUse: ConversationUse } | null {
    return this.applyAcquiescence("object_proposals", "obj_id", objId, strength, atISO);
  }

  /** As {@link recordCorrection}, for an object proposal. */
  recordObjectCorrection(objId: string, atISO?: string): boolean {
    return this.applyCorrection("object_proposals", "obj_id", objId, atISO);
  }

  private applyAcquiescence(
    table: "relation_proposals" | "object_proposals",
    idCol: "rel_id" | "obj_id",
    id: string,
    strength: AcquiescenceStrength,
    atISO: string,
  ): { counted: boolean; score: number; conversationUse: ConversationUse } | null {
    const row = this.db
      .prepare(
        `SELECT score, acquiescence_count, last_acquiesced_at, confirmed, rejected_at
           FROM ${table} WHERE ${idCol} = ?`,
      )
      .get(id) as
      | {
          score: number;
          acquiescence_count: number;
          last_acquiesced_at: string | null;
          confirmed: string | null;
          rejected_at: string | null;
        }
      | undefined;
    if (!row) return null;
    // A promoted (confirmed → in the trusted graph) or rejected proposal is settled and
    // INVIOLABLE: do NOT recompute it. Recomputing would re-derive guard_eligible from
    // the row's evidence/source and could RESURRECT the guard eligibility that reject
    // zeroed (Codex cross-model review). Return the frozen stored state untouched.
    if (row.confirmed || row.rejected_at) {
      return { counted: false, score: row.score, conversationUse: this.readConversationUse(table, idCol, id) };
    }
    const counted = !sameUtcDay(atISO, row.last_acquiesced_at);
    const delta = counted ? (strength === "strong" ? SCORE.acq_strong : SCORE.acq_weak) : 0;
    const newScore = row.score + delta;
    // A same-day repeat is NOT counted AND must not advance last_acquiesced_at — else
    // repeated same-day acquiescence would keep resetting the decay clock for free
    // (defeating time decay). Only a counted event moves the timestamp.
    const newLastAt = counted ? atISO : row.last_acquiesced_at;
    this.db
      .prepare(
        `UPDATE ${table}
           SET score = ?, acquiescence_count = acquiescence_count + ?, last_acquiesced_at = ?
         WHERE ${idCol} = ?`,
      )
      .run(newScore, counted ? 1 : 0, newLastAt, id);
    this.recomputeDerived(table, idCol, id);
    return { counted, score: newScore, conversationUse: this.readConversationUse(table, idCol, id) };
  }

  private applyCorrection(
    table: "relation_proposals" | "object_proposals",
    idCol: "rel_id" | "obj_id",
    id: string,
    _atISO?: string,
  ): boolean {
    const info = this.db
      .prepare(
        `UPDATE ${table}
           SET score = score + ?, contested = 1, acquiescence_count = 0, last_acquiesced_at = NULL
         WHERE ${idCol} = ? AND confirmed IS NULL AND rejected_at IS NULL`,
      )
      .run(SCORE.correction, id);
    if (info.changes === 0) return false;
    this.recomputeDerived(table, idCol, id);
    return true;
  }

  private readConversationUse(
    table: "relation_proposals" | "object_proposals",
    idCol: "rel_id" | "obj_id",
    id: string,
  ): ConversationUse {
    const r = this.db
      .prepare(`SELECT conversation_use FROM ${table} WHERE ${idCol} = ?`)
      .get(id) as { conversation_use: string | null } | undefined;
    return (r?.conversation_use as ConversationUse | null) ?? "low";
  }

  /**
   * Read-time effective conversationUse for a relation proposal: the stored steady
   * state, then time decay (a stale decay-prone fact drops a notch) and the
   * derived≤parent clamp (can't be used more confidently than what it was inferred
   * from). `nowISO` is the clock; pass the session time. Returns null if absent.
   */
  effectiveConversationUse(relId: string, nowISO: string): ConversationUse | null {
    return this.effectiveUseGuarded(relId, nowISO, new Set());
  }

  private effectiveUseGuarded(
    relId: string,
    nowISO: string,
    seen: Set<string>,
  ): ConversationUse | null {
    const p = this.getProposal(relId);
    if (!p) return null;
    let use = (p.conversationUse as ConversationUse | undefined) ?? "low";
    use = applyDecay(use, p.factType ?? null, p.lastAcquiescedAt ?? p.proposedAt ?? null, nowISO);
    // derived ≤ parent. Cycle-guard a malformed derived_from chain (and skip a
    // self-reference) so a bad parent pointer can never spin forever.
    if (p.derivedFrom && p.derivedFrom !== relId && !seen.has(relId)) {
      seen.add(relId);
      const parentUse = this.effectiveUseGuarded(p.derivedFrom, nowISO, seen);
      use = clampToParent(use, parentUse);
    }
    return use;
  }

  /**
   * Read-time guard eligibility for a relation proposal: the stored `guard_eligible`
   * AND a derived≤parent clamp — a proposal inferred FROM another can be guard-eligible
   * only if its parent is (a guard must not trust a derivation more than its source).
   * THIS is what a risk guard should call, not the raw column.
   *
   * Note: NO time decay here, by design. Guard eligibility is KIND-gated
   * (deterministic/explicit only), not score-gated, and those kinds aren't decay-prone
   * — so decay is irrelevant to the guard axis (unlike conversationUse). Don't "fix"
   * this by adding score/decay logic: it would only ever weaken the safety filter.
   *
   * Fails CLOSED on a malformed `derived_from` (self-reference or a cycle): the chain
   * can't be verified to ground out in a real eligible source, so the row is treated as
   * NOT guard-eligible. (The conversation path fails open on a cycle, which is harmless
   * there; on the safety axis the conservative default is the only correct one.)
   */
  effectiveGuardEligible(relId: string): boolean {
    return this.effectiveGuardGuarded(relId, new Set());
  }

  private effectiveGuardGuarded(relId: string, seen: Set<string>): boolean {
    const p = this.getProposal(relId);
    if (!p) return false;
    // A confirmed proposal's knowledge lives in the TRUSTED graph now — a guard should
    // read that, not the proposal; a rejected one is dead. Treat both as not-a-guard-input
    // here, so direct callers can't get `true` for a settled row (Codex round-2).
    if (p.confirmed || p.rejectedAt) return false;
    // Authoritative taint check: don't trust a possibly-stale stored guard_eligible (e.g.
    // a pre-existing row from before the taint existed). A conversation-touched row is
    // NEVER guard-eligible (Codex round-3).
    if (p.conversationTouched) return false;
    if (!p.guardEligible) return false;
    if (p.derivedFrom) {
      // A derived row needs a guard-eligible parent. A self-reference or a cycle
      // (already-seen) can't be verified to ground out -> FAIL CLOSED on the safety axis.
      if (p.derivedFrom === relId || seen.has(relId)) return false;
      seen.add(relId);
      if (!this.effectiveGuardGuarded(p.derivedFrom, seen)) return false; // parent not eligible -> child not
    }
    return true;
  }

  getProposal(relId: string): ProposalRow | null {
    const r = this.db
      .prepare(`SELECT * FROM relation_proposals WHERE rel_id = ?`)
      .get(relId) as ProposalDbRow | undefined;
    return r ? dbToProposal(r) : null;
  }

  /** Proposals at or above a minimum trust rank (default all), highest trust first. */
  listProposals(minRank = 0): ProposalRow[] {
    const rows = this.db
      .prepare(`SELECT * FROM relation_proposals WHERE trust_rank >= ? ORDER BY trust_rank DESC, rel_id`)
      .all(minRank) as ProposalDbRow[];
    return rows.map(dbToProposal);
  }

  /** Drop all proposals (the deliberate `enrich --rebuild`; NOT the deterministic rebuild). */
  clearProposals(): void {
    this.db.exec("DELETE FROM relation_proposals;");
  }

  // ── Phase-2: conversation evidence (append-only) + chunk state ───────────────

  /**
   * Record one piece of conversation evidence for a proposal. Idempotent: the
   * (relId, transcriptId, messageId, quoteHash) PK makes a duplicate ingest a no-op,
   * so re-runs / overlapping chunks never double-count corroboration (Codex #2).
   *
   * This table is BY DEFINITION conversation evidence (transcript/message/quote), so
   * attaching any here taints the proposal's guard axis: `conversation_touched=1` +
   * a guard recompute. This is the authoritative taint point — it catches the cases the
   * upsert merge misses (a rank-BLOCKED conflict that still attaches evidence, and any
   * direct caller) — Codex round-2 review. A settled (confirmed/rejected) row is left
   * frozen (no recompute), preserving inviolability.
   */
  addProposalEvidence(ev: ConversationEvidence): void {
    this.db
      .prepare(
        `INSERT INTO proposal_evidence
           (rel_id, transcript_id, message_id, span_start, span_end, quote_hash, speaker, run_id, message_at)
         VALUES (@relId, @transcriptId, @messageId, @spanStart, @spanEnd, @quoteHash, @speaker, @runId, @messageAt)
         ON CONFLICT(rel_id, transcript_id, message_id, quote_hash)
         DO UPDATE SET message_at = COALESCE(proposal_evidence.message_at, excluded.message_at)`,
      )
      .run({
        relId: ev.relId,
        transcriptId: ev.transcriptId,
        messageId: ev.messageId,
        spanStart: ev.spanStart ?? null,
        spanEnd: ev.spanEnd ?? null,
        quoteHash: ev.quoteHash,
        speaker: ev.speaker ?? null,
        runId: ev.runId ?? null,
        messageAt: ev.messageAt ?? null,
      });
    // Taint the proposal (if it exists as a relation_proposal) and refresh guard
    // eligibility — unless it is settled, which stays frozen.
    const p = this.getProposal(ev.relId);
    if (p && !p.confirmed && !p.rejectedAt) {
      this.db
        .prepare(`UPDATE relation_proposals SET conversation_touched = 1 WHERE rel_id = ?`)
        .run(ev.relId);
      this.recomputeDerived("relation_proposals", "rel_id", ev.relId);
    }
  }

  /** How many DISTINCT transcripts corroborate a proposal (derived, never stored). */
  corroborationCount(relId: string): number {
    const row = this.db
      .prepare(`SELECT COUNT(DISTINCT transcript_id) AS n FROM proposal_evidence WHERE rel_id = ?`)
      .get(relId) as { n: number };
    return row.n;
  }

  /** All evidence rows for a proposal (review / GC). */
  listProposalEvidence(relId: string): ConversationEvidence[] {
    const rows = this.db
      .prepare(
        `SELECT rel_id, transcript_id, message_id, span_start, span_end, quote_hash, speaker, run_id, message_at
         FROM proposal_evidence WHERE rel_id = ? ORDER BY transcript_id, message_id`,
      )
      .all(relId) as {
      rel_id: string;
      transcript_id: string;
      message_id: string;
      span_start: number | null;
      span_end: number | null;
      quote_hash: string;
      speaker: string | null;
      run_id: string | null;
      message_at: string | null;
    }[];
    return rows.map((r) => ({
      relId: r.rel_id,
      transcriptId: r.transcript_id,
      messageId: r.message_id,
      spanStart: r.span_start,
      spanEnd: r.span_end,
      quoteHash: r.quote_hash,
      speaker: r.speaker,
      runId: r.run_id,
      messageAt: r.message_at,
    }));
  }

  /** Mark a transcript chunk's processing outcome (chunk-level, not transcript-level — #9). */
  setChunkState(s: ChunkState): void {
    this.db
      .prepare(
        `INSERT INTO conversation_chunk_state
           (transcript_id, chunk_id, status, extractor_version, prompt_version, schema_version, processed_at)
         VALUES (@transcriptId, @chunkId, @status, @extractorVersion, @promptVersion, @schemaVersion, @processedAt)
         ON CONFLICT(transcript_id, chunk_id) DO UPDATE SET
           status = excluded.status, extractor_version = excluded.extractor_version,
           prompt_version = excluded.prompt_version, schema_version = excluded.schema_version,
           processed_at = excluded.processed_at`,
      )
      .run({
        transcriptId: s.transcriptId,
        chunkId: s.chunkId,
        status: s.status,
        extractorVersion: s.extractorVersion ?? null,
        promptVersion: s.promptVersion ?? null,
        schemaVersion: s.schemaVersion ?? null,
        processedAt: s.processedAt ?? null,
      });
  }

  getChunkState(transcriptId: string, chunkId: string): ChunkState | null {
    const r = this.db
      .prepare(
        `SELECT transcript_id, chunk_id, status, extractor_version, prompt_version, schema_version, processed_at
         FROM conversation_chunk_state WHERE transcript_id = ? AND chunk_id = ?`,
      )
      .get(transcriptId, chunkId) as
      | {
          transcript_id: string;
          chunk_id: string;
          status: string;
          extractor_version: string | null;
          prompt_version: string | null;
          schema_version: string | null;
          processed_at: string | null;
        }
      | undefined;
    if (!r) return null;
    return {
      transcriptId: r.transcript_id,
      chunkId: r.chunk_id,
      status: r.status as ChunkState["status"],
      extractorVersion: r.extractor_version,
      promptVersion: r.prompt_version,
      schemaVersion: r.schema_version,
      processedAt: r.processed_at,
    };
  }

  /** All recorded chunk states for a transcript (resume / coverage check). */
  chunkStatesFor(transcriptId: string): ChunkState[] {
    const rows = this.db
      .prepare(
        `SELECT transcript_id, chunk_id, status, extractor_version, prompt_version, schema_version, processed_at
         FROM conversation_chunk_state WHERE transcript_id = ? ORDER BY chunk_id`,
      )
      .all(transcriptId) as {
      transcript_id: string;
      chunk_id: string;
      status: string;
      extractor_version: string | null;
      prompt_version: string | null;
      schema_version: string | null;
      processed_at: string | null;
    }[];
    return rows.map((r) => ({
      transcriptId: r.transcript_id,
      chunkId: r.chunk_id,
      status: r.status as ChunkState["status"],
      extractorVersion: r.extractor_version,
      promptVersion: r.prompt_version,
      schemaVersion: r.schema_version,
      processedAt: r.processed_at,
    }));
  }

  /**
   * Drop all chunk-state for a transcript. Used when the transcript's content has
   * changed since it was last extracted: chunk ids are POSITIONAL, so the old rows
   * no longer correspond to the new text and must not suppress re-extraction.
   */
  deleteChunkStates(transcriptId: string): void {
    this.db.prepare(`DELETE FROM conversation_chunk_state WHERE transcript_id = ?`).run(transcriptId);
  }

  /**
   * Drop a transcript's UNCONFIRMED conversation proposals (relations + objects) and
   * their evidence. Used when the transcript's content changed: the old anchors no
   * longer hold, so stale candidates must not linger in the review/confirm queue.
   * CONFIRMED proposals are left untouched — they were already promoted (the durable
   * record under `data/ontos/` owns that knowledge); only pending candidates are purged.
   */
  deleteUnconfirmedConversationProposals(transcriptId: string): void {
    // Purge ONLY this transcript's own UNCONFIRMED, CONVERSATION-sourced candidates.
    // Never touches: confirmed proposals or their evidence; record-sourced proposals
    // (source_kind='record', which use evidence_path, not the proposal_evidence table);
    // or candidates corroborated by another, unchanged transcript. Atomic so a
    // concurrent confirm cannot interleave between the steps.
    const run = this.db.transaction((t: string) => {
      // Snapshot the rel_ids of UNCONFIRMED CONVERSATION proposals this transcript
      // anchors, BEFORE deleting its evidence — so the proposal delete is scoped to
      // exactly those, never a global sweep.
      const affected = this.db
        .prepare(
          `SELECT DISTINCT e.rel_id AS rel_id
             FROM proposal_evidence e
             JOIN relation_proposals p ON p.rel_id = e.rel_id
            WHERE e.transcript_id = ? AND p.confirmed IS NULL AND p.source_kind = 'conversation'`,
        )
        .all(t) as { rel_id: string }[];
      // 1. Drop this transcript's evidence, but ONLY for those unconfirmed conversation
      //    proposals — a confirmed proposal's evidence (and record-sourced rows) stays.
      this.db
        .prepare(
          `DELETE FROM proposal_evidence
            WHERE transcript_id = ?
              AND rel_id IN (SELECT rel_id FROM relation_proposals
                              WHERE confirmed IS NULL AND source_kind = 'conversation')`,
        )
        .run(t);
      // 2. Of those affected proposals, delete the ones now left with NO evidence at all
      //    (unanchored). One still corroborated by another transcript keeps its remaining
      //    evidence and survives.
      const del = this.db.prepare(
        `DELETE FROM relation_proposals
          WHERE rel_id = ? AND rel_id NOT IN (SELECT DISTINCT rel_id FROM proposal_evidence)`,
      );
      for (const r of affected) del.run(r.rel_id);
      // 3. Unconfirmed CONVERSATION object proposals originating from this transcript
      //    (objects carry a single-source anchor, no cross-transcript evidence table).
      this.db
        .prepare(
          `DELETE FROM object_proposals
            WHERE source_family = ? AND confirmed IS NULL AND source_kind = 'conversation'`,
        )
        .run(t);
    });
    run(transcriptId);
  }

  // ── Transcript-level extraction watermark (auto-extraction) ──────────────────

  /**
   * Mark a whole transcript as extracted, keyed by its content hash. Idempotent
   * (upsert). The caller writes this only once every chunk of the transcript has a
   * processed chunk-state; on a content change it overwrites with the new hash so
   * the transcript re-extracts. See `conversation_extraction_state` in schema.ts.
   */
  markTranscriptExtracted(s: {
    transcriptId: string;
    contentHash: string;
    proposalCount: number;
    extractorVersion?: string | null;
    extractedAt?: string | null;
    /** "maxChars/overlap" the chunks were cut with — positional chunk ids are only
     *  comparable under the same params, so a non-scan finalizer must match it. */
    chunkSig?: string | null;
  }): void {
    this.db
      .prepare(
        `INSERT INTO conversation_extraction_state
           (transcript_id, content_hash, proposal_count, extractor_version, extracted_at, chunk_sig)
         VALUES (@transcriptId, @contentHash, @proposalCount, @extractorVersion, @extractedAt, @chunkSig)
         ON CONFLICT(transcript_id) DO UPDATE SET
           content_hash = excluded.content_hash, proposal_count = excluded.proposal_count,
           extractor_version = excluded.extractor_version, extracted_at = excluded.extracted_at,
           chunk_sig = excluded.chunk_sig`,
      )
      .run({
        transcriptId: s.transcriptId,
        contentHash: s.contentHash,
        proposalCount: s.proposalCount,
        extractorVersion: s.extractorVersion ?? null,
        extractedAt: s.extractedAt ?? null,
        chunkSig: s.chunkSig ?? null,
      });
  }

  /** The extraction watermark for a transcript, or null if it was never extracted. */
  /** Record the LAST scan/emit attempt (single row, overwrites) — the opt-in
   *  gate's visible trace: 'curated-skip' when gated off, 'mined' on a real
   *  pass. `at` is caller-supplied (the store never reads the wall clock). */
  setScanStatus(s: { mode: "curated-skip" | "mined"; op: "scan" | "emit"; at: string; detail?: string | null }): void {
    this.db
      .prepare(
        `INSERT INTO extraction_scan_status (id, mode, op, at, detail) VALUES (1, ?, ?, ?, ?)
         ON CONFLICT(id) DO UPDATE SET mode = excluded.mode, op = excluded.op, at = excluded.at, detail = excluded.detail
         WHERE excluded.at >= extraction_scan_status.at`,
      )
      .run(s.mode, s.op, s.at, s.detail ?? null);
  }

  /** The last scan/emit attempt, or null when none was ever recorded. */
  getScanStatus(): { mode: string; op: string; at: string; detail: string | null } | null {
    const r = this.db
      .prepare(`SELECT mode, op, at, detail FROM extraction_scan_status WHERE id = 1`)
      .get() as { mode: string; op: string; at: string; detail: string | null } | undefined;
    return r ?? null;
  }

  getTranscriptExtraction(transcriptId: string): {
    transcriptId: string;
    contentHash: string;
    proposalCount: number;
    extractorVersion: string | null;
    extractedAt: string | null;
    chunkSig: string | null;
  } | null {
    const r = this.db
      .prepare(
        `SELECT transcript_id, content_hash, proposal_count, extractor_version, extracted_at, chunk_sig
         FROM conversation_extraction_state WHERE transcript_id = ?`,
      )
      .get(transcriptId) as
      | {
          transcript_id: string;
          content_hash: string;
          proposal_count: number;
          extractor_version: string | null;
          extracted_at: string | null;
          chunk_sig: string | null;
        }
      | undefined;
    if (!r) return null;
    return {
      transcriptId: r.transcript_id,
      contentHash: r.content_hash,
      proposalCount: r.proposal_count,
      extractorVersion: r.extractor_version,
      extractedAt: r.extracted_at,
      chunkSig: r.chunk_sig,
    };
  }

  // ── Phase-2 (MVP-B): object proposals (NEW entities from conversation) ────────

  /** Store an object proposal in `object_proposals` — NEVER `objects`. Idempotent + monotonic. */
  upsertObjectProposal(p: ObjectProposal): "inserted" | "upgraded" | "refreshed" | "demoted" | "kept" {
    const before = this.getObjectProposal(p.objId);
    const evidenceKind: EvidenceKind = p.evidenceKind ?? evidenceKindFromVerifiedBy(p.verifiedBy);
    this.db
      .prepare(
        `INSERT INTO object_proposals
           (obj_id, type, label, trust, trust_rank, verified_by, confidence,
            source_kind, source_family, evidence_path, evidence_span, extraction_run_id, proposed_at, confirmed,
            evidence_kind, fact_type, derived_from, score, conversation_touched)
         VALUES
           (@objId, @type, @label, @trust, @trustRank, @verifiedBy, @confidence,
            @sourceKind, @sourceFamily, @evidencePath, @evidenceSpan, @extractionRunId, @proposedAt, @confirmed,
            @evidenceKind, @factType, @derivedFrom, @scoreSeed, @conversationTouched)
         ON CONFLICT(obj_id) DO UPDATE SET
           trust = excluded.trust, trust_rank = excluded.trust_rank, verified_by = excluded.verified_by,
           confidence = excluded.confidence, label = excluded.label,
           evidence_path = excluded.evidence_path, evidence_span = excluded.evidence_span,
           extraction_run_id = excluded.extraction_run_id,
           evidence_kind = COALESCE(excluded.evidence_kind, object_proposals.evidence_kind),
           fact_type = COALESCE(excluded.fact_type, object_proposals.fact_type),
           derived_from = COALESCE(excluded.derived_from, object_proposals.derived_from),
           -- F3 demotion (see upsertProposal): reset score to the new seed on a genuine
           -- same-source downgrade with no user signal; else keep the floor via MAX.
           score = CASE
             WHEN excluded.trust_rank < object_proposals.trust_rank AND object_proposals.acquiescence_count = 0
               THEN excluded.score
             ELSE MAX(object_proposals.score, excluded.score)
           END,
           conversation_touched = MAX(object_proposals.conversation_touched, excluded.conversation_touched)
         -- Inviolable: a confirmed/rejected object proposal is never churned by re-extraction.
         WHERE object_proposals.confirmed IS NULL AND object_proposals.rejected_at IS NULL
           AND (
             excluded.trust_rank >= object_proposals.trust_rank
             -- F3 demotion path (objects have NO per-source evidence table, so the
             -- corroboration NOT EXISTS guard is unavailable): allow a same-source
             -- conversation downgrade, guarded by evidence_path (the row's last writer is
             -- still this transcript — a row since refreshed by another source has moved
             -- its path off) + trust_rank <= 2 + source_kind. KNOWN LIMITATION: two
             -- transcripts both cross-modeling one object then the first re-extracting
             -- self is not caught (rare, non-safety; objects are never guard-eligible).
             OR (
               excluded.trust_rank < object_proposals.trust_rank
               AND object_proposals.trust_rank <= 2
               AND object_proposals.source_kind = 'conversation' AND excluded.source_kind = 'conversation'
               AND excluded.source_family IS NOT NULL
               AND excluded.source_family = object_proposals.source_family
               AND excluded.evidence_path = object_proposals.evidence_path
             )
           )`,
      )
      .run({
        objId: p.objId,
        type: p.type,
        label: p.label,
        trust: p.trust,
        trustRank: p.trustRank,
        verifiedBy: p.verifiedBy,
        confidence: p.confidence,
        sourceKind: p.sourceKind ?? "conversation",
        sourceFamily: p.sourceFamily ?? null,
        evidencePath: p.evidencePath ?? null,
        evidenceSpan: p.evidenceSpan ?? null,
        extractionRunId: p.extractionRunId ?? null,
        proposedAt: p.proposedAt ?? null,
        confirmed: p.confirmed ?? null,
        evidenceKind,
        factType: p.factType ?? null,
        derivedFrom: p.derivedFrom ?? null,
        scoreSeed: p.score ?? initialScore(evidenceKind),
        conversationTouched: (p.sourceKind ?? "conversation") === "conversation" ? 1 : 0,
      });
    if (!(before && (before.confirmed || before.rejectedAt))) {
      // Rank-independent sticky taint (see upsertProposal, round-4).
      if ((p.sourceKind ?? "conversation") === "conversation") {
        this.db.prepare(`UPDATE object_proposals SET conversation_touched = 1 WHERE obj_id = ?`).run(p.objId);
      }
      this.recomputeDerived("object_proposals", "obj_id", p.objId);
    }
    if (!before) return "inserted";
    if (p.trustRank > before.trustRank) return "upgraded";
    if (p.trustRank === before.trustRank) return "refreshed";
    return (this.getObjectProposal(p.objId)?.trustRank ?? before.trustRank) < before.trustRank ? "demoted" : "kept";
  }

  getObjectProposal(objId: string): ObjectProposal | null {
    const r = this.db.prepare(`SELECT * FROM object_proposals WHERE obj_id = ?`).get(objId) as
      | Record<string, unknown>
      | undefined;
    return r ? dbToObjectProposal(r) : null;
  }

  /** Unconfirmed, un-rejected object proposals, highest trust first (the review surface). */
  listObjectProposals(minRank = 0): ObjectProposal[] {
    const rows = this.db
      .prepare(
        `SELECT * FROM object_proposals
         WHERE trust_rank >= ? AND confirmed IS NULL AND rejected_at IS NULL
         ORDER BY trust_rank DESC, obj_id`,
      )
      .all(minRank) as Record<string, unknown>[];
    return rows.map(dbToObjectProposal);
  }

  /**
   * Promote an object proposal into the trusted `objects` set — the human "yes, this
   * is a real entity". After this, a relation proposal referencing this id can be
   * confirmed (confirmProposal requires both endpoints to exist). Returns true on promote.
   */
  confirmObjectProposal(objId: string, date: string): boolean {
    const p = this.getObjectProposal(objId);
    if (!p || p.confirmed || p.rejectedAt) return false;
    this.upsertObject({ id: p.objId, type: p.type, label: p.label, source: p.evidencePath ?? "conversation" });
    this.db.prepare(`UPDATE object_proposals SET confirmed = ? WHERE obj_id = ?`).run(date, objId);
    return true;
  }

  /** Sticky reject an object proposal (not re-surfaced). */
  rejectObjectProposal(objId: string, date: string): boolean {
    const p = this.getObjectProposal(objId);
    if (!p || p.confirmed || p.rejectedAt) return false;
    // Zero guard_eligible on reject (defense in depth — see rejectProposal).
    this.db
      .prepare(`UPDATE object_proposals SET rejected_at = ?, guard_eligible = 0 WHERE obj_id = ?`)
      .run(date, objId);
    return true;
  }

  /**
   * Confirmed CONVERSATION-sourced objects/relations — the durable-record source.
   * Conversation knowledge has no markdown of its own, so a rebuild's clear() would
   * wipe it; these feed a synthesized `data/ontos/` record that the rebuild re-reads,
   * so confirmed conversation knowledge survives + syncs (the Phase-2 durability fix).
   */
  confirmedConversationObjects(): { id: string; type: string; label: string }[] {
    return this.db
      .prepare(
        `SELECT obj_id AS id, type, label FROM object_proposals
         WHERE confirmed IS NOT NULL AND source_kind = 'conversation' ORDER BY obj_id`,
      )
      .all() as { id: string; type: string; label: string }[];
  }

  confirmedConversationRelations(): {
    relId: string;
    rel: string;
    source: string;
    target: string;
    confirmed: string;
    evidence: string | null;
  }[] {
    return this.db
      .prepare(
        `SELECT rel_id AS relId, rel, source_id AS source, target_id AS target, confirmed,
                COALESCE(source_family, evidence_path) AS evidence
         FROM relation_proposals
         WHERE confirmed IS NOT NULL AND source_kind = 'conversation' ORDER BY rel_id`,
      )
      .all() as { relId: string; rel: string; source: string; target: string; confirmed: string; evidence: string | null }[];
  }

  /**
   * Unconfirmed, un-rejected proposals touching an object — the "possible (unverified)"
   * hint surface for the actions to weave in. Confirmed proposals are already trusted
   * edges in `relations`; rejected ones are suppressed. Highest trust first.
   */
  proposalsFor(objectId: string): ProposalRow[] {
    const rows = this.db
      .prepare(
        `SELECT * FROM relation_proposals
         WHERE (source_id = ? OR target_id = ?) AND confirmed IS NULL AND rejected_at IS NULL
         ORDER BY trust_rank DESC, rel_id`,
      )
      .all(objectId, objectId) as ProposalDbRow[];
    return rows.map(dbToProposal);
  }

  /**
   * GUARD-eligible pending proposals touching an object — the RISK path's ONLY proposal
   * query (pre-commit / deploy / recurring-failure warnings). Filtered through
   * `effectiveGuardEligible`, so a row reaches a guard only when it is
   * deterministic/explicit, record-sourced, uncontested, AND every ancestor it was
   * derived from is itself guard-eligible. Acquiescence / self / cross_model /
   * conversation-sourced knowledge can NEVER appear here — that is the safety line
   * (cheap to be wrong about in dialogue, expensive in a guard). The conversation/hedge
   * path uses `proposalsFor` instead, and reads everything (hedged by conversationUse).
   */
  guardEligibleProposalsFor(objectId: string): ProposalRow[] {
    return this.proposalsFor(objectId).filter((p) => this.effectiveGuardEligible(p.relId));
  }

  /** True if any conversation evidence is already attached to this proposal id. */
  private hasConversationEvidence(relId: string): boolean {
    return !!this.db.prepare(`SELECT 1 FROM proposal_evidence WHERE rel_id = ? LIMIT 1`).get(relId);
  }

  /**
   * Promote a proposal into the TRUSTED graph — the in-context "yes, that's right"
   * (NOT a batch curation chore). Writes the relation with provenance="llm" + a
   * `confirmed` date (the upsertRelation guard allows a CONFIRMED llm relation), and
   * marks the proposal confirmed. Refuses unless BOTH endpoints already exist as
   * objects (you can only confirm a link between known things). Returns true on promote.
   */
  confirmProposal(relId: string, date: string): boolean {
    const p = this.getProposal(relId);
    if (!p || p.confirmed || p.rejectedAt) return false;
    const src = this.getObject(p.sourceId);
    const tgt = this.getObject(p.targetId);
    if (!src || !tgt) return false; // unresolved ends — can't promote a link between unknown things
    // Re-check domain/range against the objects' STORED types (not the id prefix):
    // upsertObject does not enforce prefix==type, so a mismatched/corrupt object
    // must not let a wrong-type relation into the trusted graph at promote time
    // (Codex review — the id-prefix invariant is conventional, not enforced).
    if (domainRangeViolated(p.rel, src.type, tgt.type)) return false;
    this.transaction(() => {
      // Never overwrite a relation already in the trusted graph — a deterministic
      // (or already-confirmed) edge for this triple stays as-is; converting it to
      // provenance="llm" would corrupt it and break HIGH detection (Codex review).
      // The trusted edge already exists, so confirmation just records the proposal.
      if (this.relationProvenance(p.relId) === null) {
        this.upsertRelation({
          relId: p.relId,
          rel: p.rel,
          sourceId: p.sourceId,
          targetId: p.targetId,
          provenance: "llm",
          confidence: p.confidence,
          confirmed: date,
          evidence: p.evidencePath || null,
        });
      }
      this.db.prepare(`UPDATE relation_proposals SET confirmed = ? WHERE rel_id = ?`).run(date, relId);
    });
    return true;
  }

  /** Reject a proposal (sticky): it is never promoted, surfaced as a hint, or re-asked. */
  rejectProposal(relId: string, date: string): boolean {
    // Zero guard_eligible on reject: a rejected proposal must never feed a risk guard,
    // even if a downstream reader forgets to filter rejected_at (defense in depth).
    const r = this.db
      .prepare(
        `UPDATE relation_proposals SET rejected_at = ?, guard_eligible = 0
         WHERE rel_id = ? AND confirmed IS NULL AND rejected_at IS NULL`,
      )
      .run(date, relId);
    return r.changes > 0;
  }

  /** Stamp that a proposal was just surfaced as an in-context question (re-ask cooldown). */
  markAsked(relId: string, date: string): void {
    this.db.prepare(`UPDATE relation_proposals SET asked_at = ? WHERE rel_id = ?`).run(date, relId);
  }

  // ---- GC / reconcile (the documented orphan cleanup) ----
  // The store can't read the filesystem (it's pure sqlite); the caller (the
  // `enrich-ontos --gc` walk) decides which record paths are dead (no longer on
  // disk) and passes them here. We only ever drop UNCONFIRMED, UN-rejected
  // proposals: a confirmed proposal was promoted into the trusted graph and a
  // rejected one is a human decision — both are kept.

  /** All record paths that have an enrichment_state row (for the GC liveness check). */
  allEnrichmentPaths(): string[] {
    return (
      this.db.prepare(`SELECT record_path FROM enrichment_state`).all() as { record_path: string }[]
    ).map((r) => r.record_path);
  }

  /** Transcript ids with a COMPLETED conversation extraction (extracted_at set) — id-level backlog math. */
  extractedTranscriptIds(): string[] {
    return (
      this.db
        .prepare(
          `SELECT transcript_id FROM conversation_extraction_state WHERE extracted_at IS NOT NULL ORDER BY transcript_id`,
        )
        .all() as { transcript_id: string }[]
    ).map((r) => r.transcript_id);
  }

  /** Drop the enrichment_state row for a vanished record (so it re-enriches if it returns). */
  deleteEnrichmentState(recordPath: string): boolean {
    return this.db.prepare(`DELETE FROM enrichment_state WHERE record_path = ?`).run(recordPath).changes > 0;
  }

  /** Distinct evidence_path values across UNCONFIRMED, UN-rejected proposals (GC candidates). */
  pendingProposalEvidencePaths(): string[] {
    return (
      this.db
        .prepare(
          `SELECT DISTINCT evidence_path FROM relation_proposals
           WHERE confirmed IS NULL AND rejected_at IS NULL AND evidence_path IS NOT NULL`,
        )
        .all() as { evidence_path: string }[]
    ).map((r) => r.evidence_path);
  }

  /** Drop UNCONFIRMED, UN-rejected proposals whose evidence record vanished. Returns count dropped. */
  dropPendingProposalsByEvidence(evidencePath: string): number {
    return this.db
      .prepare(
        `DELETE FROM relation_proposals
         WHERE evidence_path = ? AND confirmed IS NULL AND rejected_at IS NULL`,
      )
      .run(evidencePath).changes;
  }

  /** Change detection: the extraction hash last enriched for a record, or null. */
  getExtractionHash(recordPath: string): string | null {
    const row = this.db
      .prepare(`SELECT extraction_hash FROM enrichment_state WHERE record_path = ?`)
      .get(recordPath) as { extraction_hash: string } | undefined;
    return row ? row.extraction_hash : null;
  }

  setExtractionState(recordPath: string, extractionHash: string, enrichedAt?: string | null): void {
    this.db
      .prepare(
        `INSERT INTO enrichment_state (record_path, extraction_hash, enriched_at)
         VALUES (?, ?, ?)
         ON CONFLICT(record_path) DO UPDATE SET
           extraction_hash = excluded.extraction_hash, enriched_at = excluded.enriched_at`,
      )
      .run(recordPath, extractionHash, enrichedAt ?? null);
  }

  /** §5A helper: how many failures recur as (share a pattern with) this one. */
  recurrenceCount(problemId: string): number {
    const row = this.db
      .prepare(
        `SELECT COUNT(*) AS n FROM relations WHERE rel = 'recurs-as' AND (source_id = ? OR target_id = ?)`,
      )
      .get(problemId, problemId) as { n: number };
    return row.n;
  }
}
